import { Badge } from '@artic/ui/badge';
import { FullHeightChrome, FullHeightFill, FullHeightStack } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import {
  projectBreadcrumbBadges,
  projectHeaderActions,
} from '../../features/project-planning/project-route-chrome';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import { projectPlanningDetailQueryOptions } from '../../features/project-planning/queries';
import { ProjectTimeline } from '../../features/project-timeline/project-timeline';
import { ProjectTimelineViewBy } from '../../features/project-timeline/project-timeline-filter-bar';

export const Route = createFileRoute('/projects/$projectId_/timeline')({
  staticData: {
    // Carries the project name, matching the release / redraft / run-sheets /
    // gantt siblings. ENG-720 retired the page-level ProjectPageHeader that
    // used to name the project, so a bare 'Timeline' crumb would leave the
    // page with no indication of which project is on screen.
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Timeline` : 'Timeline';
    },
    breadcrumbBadges: projectBreadcrumbBadges,
    headerActions: projectHeaderActions,
    layout: 'full-height',
  },
  component: ProjectTimelineContent,
});

function ProjectTimelineContent() {
  const { projectId } = Route.useParams();

  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  if (projectLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project. Please try again.
        </p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <Badge variant="outline">Project not found</Badge>
      </div>
    );
  }

  const isProjectRedrafting = project.project_status === 'redrafting';

  return (
    <FullHeightStack>
      <FullHeightChrome>
        <ProjectStepper
          projectId={project.project_id}
          active="work_orders"
          projectStatus={project.project_status}
          isProjectRedrafting={isProjectRedrafting}
        />
      </FullHeightChrome>
      <FullHeightChrome className="flex items-center justify-end px-3 py-1.5">
        <ProjectTimelineViewBy />
      </FullHeightChrome>
      <FullHeightFill className="px-3 pb-3">
        <ProjectTimeline
          projects={[
            {
              project_id: project.project_id,
              project_name: project.project_name,
              project_derived_status: project.project_derived_status,
              project_scheduled_start_date: project.project_scheduled_start_date,
              project_scheduled_complete_date: project.project_scheduled_complete_date,
            },
          ]}
          applyStatusFilter={false}
        />
      </FullHeightFill>
    </FullHeightStack>
  );
}
